/**
 * Maps helpdesk tickets from `/api/helpdesk/tickets` into Request/Approvals desk
 * entries so they render alongside Leave and onboarding items in the sidebar desk.
 */

export const HELPDESK_DESK_PREFIX = "helpdesk_";
export const TICKET_ACTION_LABEL = "Ticket";

export const isHelpdeskDeskRequest = (request) =>
  String(request?.requestId || "").startsWith(HELPDESK_DESK_PREFIX);

export const helpdeskTicketIdFromRequestId = (requestId) => {
  const raw = String(requestId || "").slice(HELPDESK_DESK_PREFIX.length);
  const id = Number(raw);
  return Number.isFinite(id) ? id : null;
};

/** Ticket lifecycle status → desk tab (Pending / Approved / Rejected). */
export const deskStatusForTicket = (ticket) => {
  if (ticket?.status === "closed") {
    return ticket?.rcaJson?.rejected ? "Rejected" : "Approved";
  }
  if (ticket?.status === "resolved") return "Approved";
  return "Pending";
};

export function mapHelpdeskTicketToDeskItem(ticket, { panelKey = "review" } = {}) {
  const submittedAt = ticket.createdAt ? new Date(ticket.createdAt).getTime() : Date.now();
  const status = deskStatusForTicket(ticket);
  const reminders = Array.isArray(ticket.remindersJson) ? ticket.remindersJson : [];
  const audit = Array.isArray(ticket.auditJson) ? ticket.auditJson : [];

  return {
    requestId: `${HELPDESK_DESK_PREFIX}${ticket.id}`,
    panelKey,
    actionLabel: TICKET_ACTION_LABEL,
    deskMode: "helpdesk",
    helpdeskTicketId: ticket.id,
    employeeId: ticket.employeeId != null ? String(ticket.employeeId) : null,
    userId: ticket.raisedByUserId || null,
    userName: ticket.raisedByName || ticket.employeeName || "Requester",
    userEmail: ticket.raisedByEmail || "",
    status,
    submittedAt,
    updatedAt: ticket.updatedAt ? new Date(ticket.updatedAt).getTime() : submittedAt,
    pendingWith: status === "Pending" ? ticket.pendingWith || "Helpdesk Queue" : null,
    canAct: !!ticket.canAct,
    isRaiser: !!ticket.isRaiser,
    ticket,
    formData: {
      ticketNo: ticket.ticketNo,
      ticketType: ticket.typeName || ticket.typeId || "",
      ticketCategory: ticket.categoryName || ticket.categoryId || "",
      ticketSubcategory: ticket.subcategoryName || ticket.subcategoryId || "",
      ticketPriority: ticket.priorityName || ticket.priorityId || "",
      ticketStatus: ticket.statusName || ticket.status || "",
      ticketSubject: ticket.title || "",
      assignee: ticket.pendingWith || "",
      resolutionDue: ticket.slaJson?.resolutionDue || "",
      reason: ticket.description || "",
      notes: ticket.resolutionNotes || "",
      reminders,
      feedback: ticket.feedbackJson || null,
    },
    workflowTrail: audit.map((entry) => ({
      status: entry.action,
      actor: entry.by,
      actedAt: entry.at ? new Date(entry.at).getTime() : submittedAt,
      reason: entry.reason || entry.note || entry.comments || "",
    })),
  };
}

/**
 * Merges the "raised by me" and "pending my action" ticket lists into one desk
 * collection; a ticket the user both raised and owns stays in the Request panel.
 */
export function buildHelpdeskDeskItems({ mine = [], pendingMyAction = [] } = {}) {
  const byId = new Map();
  for (const ticket of pendingMyAction) {
    byId.set(ticket.id, mapHelpdeskTicketToDeskItem(ticket, { panelKey: "approvals" }));
  }
  for (const ticket of mine) {
    byId.set(ticket.id, mapHelpdeskTicketToDeskItem(ticket, { panelKey: "review" }));
  }
  return [...byId.values()];
}
